import axios from "axios";
import lodash, { isEmpty } from "lodash-es";
import Config from "../config";
import Storage from "../utils/Storage";
import moment from "moment";
import { download } from "../utils/common";
import { getToken } from "./service";
import { customerCode } from "../components/WebIM/polyfills";

export const jsonRegex = /^\s*\{[\s\S]*\}\s*$/;

export enum NavState {
    Rules, //成绩规则
    Paper //成绩单
}

export interface RuleModelItem {
    key: number;
    id: number | null; //考核模块id
    ruleId: number | null; //所属考核id
    ruleName: string; //考核模块名称
    weight: number; //权重
    description: string; //成绩规则说明
    formulaModeJson: string; //成绩细则json(由前端自定义，告诉后端就行)
    type: BooleanValue; //类型：0平时成绩 1期末成绩
    fixed: BooleanValue; //是否固定: 0否1是
    delStatus: number; //删除标识  0否1是
    calculateClass?: string;
    flag?: string; //标记内置考核
    remark?: string; //备注
}

export interface RuleItem {
    id?: number; //成绩规则id
    visible: BooleanValue; //是否可见（0否1是）
    isProcess: BooleanValue; //是否是过程化考核（0否1是）
    dailyGrade: number; //平时成绩占比
    finalGrade: number; //期末成绩占比
    customerCode: string; //租户CODE
    batchNo: string; //学期编号
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    delStatus: number; //逻辑删除 0：未删除 1：已删除
    description: string;
    filePath: string; //文件地址
    fileType: string; //类型
    fileName: string; //文件名
    canEdit: BooleanValue; //是否可编辑;
}

export interface ExtraRule {
    config: string | string[]; //加分项，由,号隔开目前会有两个固定值 discuss（主题讨论）、other（其他）
    gtHundred: BooleanValue; //总分能否超过100
}

export type ExtraType = null | undefined | ExtraRule;
export interface Rules extends RuleItem {
    dailyGradeRuleModel: RuleModelItem[];
    finalGradeRuleModel: RuleModelItem[];
    extraRule: ExtraType; //额外加分
}

/**
 * 查询对应课程下的成绩规则（教师端）
 * @param courseNo 课程编号
 * @param courseSeq 课程序号
 */

export function queryRule(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;

    return axios
        .post<SuccessData<Rules>>("/teacher/rule/queryRule", {
            courseNo,
            courseSeq,
            batchNo,
            customerCode
        })
        .then(res => res.data.data)
        .then(data => {
            const dailyGradeRuleModel = data.dailyGradeRuleModel.map(
                (item, index) => {
                    let formulaModeJson = item.formulaModeJson;
                    if (item.calculateClass?.includes("SignInCalculator")) {
                        if (!formulaModeJson || formulaModeJson === "") {
                            formulaModeJson = `{\"done\":1,\"leave\":0,\"late\":0,\"otherRules\":[]}`;
                        }
                    }
                    return {
                        ...item,
                        formulaModeJson,
                        key: index,
                        flag: item.calculateClass?.split(".").pop(),
                        remark:
                            item.remark && jsonRegex.test(item.remark)
                                ? ""
                                : item.remark
                    };
                }
            );

            const finalGradeRuleModel = data.finalGradeRuleModel.map(
                (item, index) => ({
                    ...item,
                    key: index,
                    flag: item.calculateClass?.split(".").pop(),
                    remark:
                        item.remark && jsonRegex.test(item.remark)
                            ? ""
                            : item.remark
                })
            );

            if (typeof data?.extraRule?.config === "string") {
                data.extraRule.config = data.extraRule.config.split(",");
            }

            return { ...data, dailyGradeRuleModel, finalGradeRuleModel };
        });
}

export function queryRuleOrigin(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;

    return axios
        .post<SuccessData<Rules>>("/teacher/rule/queryRule", {
            courseNo,
            courseSeq,
            batchNo,
            customerCode
        })
        .then(res => res.data.data);
}

export interface CourseListItem {
    id: string; //课程id
    courseno: string; //课程编号
    courseseq: string; //课程序号
    coursename: string; //课程名称
    customerCode: string; //租户代码
    batchNo: string; //学期编号
}

/**
 * 查询课程列表
 * @returns
 */
export function findCourseList(keyword: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const obj = {
        batchNo,
        keyword,
        remark: "1"
    };
    return axios
        .post<SuccessData<CourseListItem[]>>(
            `/teacher/rule/findCourseList`,
            obj
        )
        .then(res => res.data.data)
        .then(data => data.map(item => ({ ...item, id: item.id + "" })));
}

/**
 * 复制成绩规则
 * @param courseIds
 * @param ruleId
 */

export function copyRules(courseIds: string[] | number[], ruleId: string) {
    return axios.post(`/teacher/rule/copy`, { courseIds, ruleId });
}

export const ruleItemMap = {
    ruleName: "模块名称",
    weight: "权重",
    description: "成绩规则"
};

export interface NewRuleModelParams
    extends Omit<RuleModelItem, "calculateClass" | "key" | "flag"> {}

export interface RuleParams extends RuleItem {
    dailyGradeRuleModel: NewRuleModelParams[];
    finalGradeRuleModel: NewRuleModelParams[];
    extraRule: ExtraType; //额外加分
}

/**
 * 保存课程下的成绩规则
 * @param params
 */
export function saveRule(params: RuleParams) {
    if (params.extraRule && Array.isArray(params.extraRule.config)) {
        params.extraRule.config = params.extraRule.config.join(",");
    }
    return axios.post(`/teacher/rule/saveRule`, params);
}

/**
 * 筛选出可见的数据
 * @param data
 * @returns
 */
export const filterData = (data: RuleModelItem[]) => {
    return data.filter(item => item?.delStatus === 0);
};

/**
 * 获取数据项，排除key字段
 */
export const getData = (data: RuleModelItem[]) => {
    const dailyData = lodash.cloneDeep(data);
    dailyData.map(item => {
        delete item.key;
    });
    return dailyData.filter(data => {
        return ((data.ruleName && data.description) ?? "") !== "";
    });
};

/**
 * 还原
 * @param courseNo
 * @param courseSeq
 */
export function resetDcGradeRule(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;

    return axios.post(`/teacher/rule/resetDcGradeRule`, {
        courseNo,
        courseSeq,
        batchNo,
        customerCode
    });
}

export interface OtherRulesItem {
    rangeVal1: number; //参数1
    sign1: string; //符号1
    rangeVal2: number; //参数2
    sign2: string; //符号2
    grade: number; //得分
}

export interface AdvancedConfig {
    done?: number; //正常出勤
    late?: number; //迟到，早退
    leave?: number; //请假
    otherRules?: OtherRulesItem[];
}

export interface AllConfig extends AdvancedConfig {
    detailWeight?: queryGradeRuleItem[];
}

export const easyMap = {
    按出勤次数折算成对应得分: "出勤次数",
    按出勤率折算成对应得分: "出勤率(%)",
    按投票参与率折算对应得分: "投票参与率(%)",
    按投票参与次数折算对应得分: "投票参与次数",
    按所有练习的平均分计分: "练习平均分",
    按练习参与次数折算成对应得分: "练习参与次数",
    按所有作业的平均分计分: "作业平均分",
    按作业参与次数折算成对应得分: "作业参与次数",
    按所有小组活动平均分计分: "小组活动平均分",
    按小组活动参与次数折算成对应得分: "小组活动参与次数",
    按所有考试平均分计分: "考试平均分",
    按考试参与次数折算成对应得分: "考试参与次数"
};

export const SelectMap: Record<
    string,
    {
        title: Record<string, string>;
        description: Record<string, string>;
        rulesSelectOption: { label: string; value: string }[];
    }
> = {
    //签到
    SignInCalculator: {
        title: {
            按出勤次数折算成对应得分: "出勤次数",
            按出勤率折算成对应得分: "出勤率(%)"
        },
        description: {
            按出勤次数折算成对应得分:
                "默认出勤次数按比例折算成得分，如需其他设置，请点击",
            按出勤率折算成对应得分:
                "默认出勤率按比例折算成得分，如需其他设置，请点击"
        },
        rulesSelectOption: [
            {
                label: "按出勤次数折算成对应得分",
                value: "按出勤次数折算成对应得分"
            },
            { label: "按出勤率折算成对应得分", value: "按出勤率折算成对应得分" }
        ]
    },
    //投票
    VoteRateCalculator: {
        title: {
            按投票参与率折算对应得分: "投票参与率(%)",
            按投票参与次数折算对应得分: "投票参与次数"
        },
        description: {
            按投票参与率折算对应得分:
                "默认投票率按比例折算成得分，如需其他设置，请点击",
            按投票参与次数折算对应得分:
                "默认投票次数按比例折算成得分，如需其他设置，请点击"
        },
        rulesSelectOption: [
            {
                label: "按投票参与率折算对应得分",
                value: "按投票参与率折算对应得分"
            },
            {
                label: "按投票参与次数折算对应得分",
                value: "按投票参与次数折算对应得分"
            }
        ]
    },
    //练习
    ExerciseScoreCalculator: {
        title: {
            按所有练习的平均分计分: "练习平均分",
            按练习参与次数折算成对应得分: "练习参与次数"
        },
        description: {
            按所有练习的平均分计分:
                "默认将全部练习的平均成绩按比例折算成得分，如需其他设置，请点击",
            按练习参与次数折算成对应得分:
                "默认按练习参与次数折算成对应得分，如需其他设置，请点击"
        },
        rulesSelectOption: [
            {
                label: "按所有练习的平均分计分",
                value: "按所有练习的平均分计分"
            },
            {
                label: "按练习参与次数折算成对应得分",
                value: "按练习参与次数折算成对应得分"
            }
        ]
    },
    //作业
    HomeworkScoreCalculator: {
        title: {
            按所有作业的平均分计分: "作业平均分",
            按作业参与次数折算成对应得分: "作业参与次数"
        },
        description: {
            按所有作业的平均分计分:
                "默认将全部作业的平均成绩按比例折算成得分，如需其他设置，请点击",
            按作业参与次数折算成对应得分:
                "默认按作业参与次数折算成对应得分，如需其他设置，请点击"
        },
        rulesSelectOption: [
            {
                label: "按所有作业的平均分计分",
                value: "按所有作业的平均分计分"
            },
            {
                label: "按作业参与次数折算成对应得分",
                value: "按作业参与次数折算成对应得分"
            }
        ]
    },
    //小组活动
    GroupActivityScoreCalculator: {
        title: {
            按所有小组活动平均分计分: "小组活动平均分",
            按小组活动参与次数折算成对应得分: "小组活动参与次数"
        },
        description: {
            按所有小组活动平均分计分:
                "默认将全部小组活动的平均成绩按比例折算成得分，如需其他设置，请点击",
            按小组活动参与次数折算成对应得分:
                "默认按小组活动参与次数折算成对应得分，如需其他设置，请点击"
        },
        rulesSelectOption: [
            {
                label: "按所有小组活动平均分计分",
                value: "按所有小组活动平均分计分"
            },
            {
                label: "按小组活动参与次数折算成对应得分",
                value: "按小组活动参与次数折算成对应得分"
            }
        ]
    },
    //小组活动
    DailyExamCalculator: {
        title: {
            按所有考试平均分计分: "考试平均分",
            按考试参与次数折算成对应得分: "考试参与次数"
        },
        description: {
            按所有考试平均分计分:
                "默认将全部考试的平均成绩按比例折算成得分，请在明细分配中将期末考试权重设为0",
            按考试参与次数折算成对应得分:
                "默认按小组活动参与次数折算成对应得分，如需其他设置，请点击"
        },
        rulesSelectOption: [
            {
                label: "按所有考试平均分计分",
                value: "按所有考试平均分计分"
            },
            {
                label: "按考试参与次数折算成对应得分",
                value: "按考试参与次数折算成对应得分"
            }
        ]
    },
    //主题讨论
    DiscussScoreCalculator: {
        title: {
            按主题讨论参与率计分: "讨论参与率(%)"
        },
        description: {
            按主题讨论参与率计分:
                "默认按全部主题讨论参与率折算成得分，如需其他设置，请点击"
        },
        rulesSelectOption: [
            {
                label: "按主题讨论参与率计分",
                value: "按主题讨论参与率计分"
            }
        ]
    }
};

export const detailWeight: Record<
    string,
    "homework" | "exam" | "exercise" | "groupActivity"
> = {
    ExerciseScoreCalculator: "exercise", //练习
    HomeworkScoreCalculator: "homework", //作业
    GroupActivityScoreCalculator: "groupActivity", //小组活动
    DailyExamCalculator: "exam",
    ExamScoreCalculator: "exam"
};

/**
 * 还原
 * @param courseNo
 * @param courseSeq
 */
export interface queryGradeRuleItem {
    id: string;
    name: string;
    createTime: string;
    weight: number;
}

interface queryGradeRuleParams {
    courseNo: string; // 课程no
    courseSeq: string; //课程seq
}
export function queryForGradeRule(
    type: "homework" | "exam" | "exercise" | "groupActivity",
    param: queryGradeRuleParams
) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;

    return axios
        .post(`/teacher/rule/queryForGradeRule`, {
            type,
            param: { ...param, batchNo, customerCode: customerCode }
        })
        .then(res => res.data.data)
        .then(data => {
            let arr = data.map((item: any) => {
                if (type === "homework") {
                    return {
                        id: item.paperId,
                        name: item.homeworkName,
                        createTime: item.createTime,
                        weight: 0
                    };
                }
                if (type === "groupActivity") {
                    return {
                        id: item.id + "",
                        name: item.activityName,
                        createTime: moment(item.createTime).format(
                            "YYYY-MM-DD HH:mm:ss"
                        ),
                        weight: 0
                    };
                }

                if (type === "exercise") {
                    return {
                        id: item.id + "",
                        name: item.description,
                        createTime: item.createTime,
                        weight: 0
                    };
                }

                return {
                    id: item.examId + "",
                    name: item.name,
                    createTime: item.startTime,
                    weight: 0
                };
            });
            return arr;
        });
}

export enum signType {
    lt = "0", //小于
    lessEqual = "1", //小于等于
    equal = "2", //等于
    moreEqual = "3", //大于
    gt = "4" //大于等于
}

export const SignMap: Record<string, string> = {
    [signType.lt]: "<",
    [signType.lessEqual]: "≤",
    [signType.equal]: "=",
    [signType.moreEqual]: "≥",
    [signType.gt]: ">"
};

export interface ColumnsItem {
    title: string; //字段标题
    weight: string; //权重
    dataIndex: string; //数据定位
    key: string; //key
    isCustom: string; //是否是固定的0否1是
}

export interface GradeItemObjProps {
    originalScore: number;
    score: number;
}

export type GradeItemProps = string | number | GradeItemObjProps;

interface Grade {
    columns: ColumnsItem[]; //字段列表
    dataSource: Record<string, GradeItemProps>[]; //数据集
    totalSize: number;
}

interface GradeParams {
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    keyword?: string; //查询关键字
    pageNo?: number; //页码
    pageSize?: number; //页面尺寸
}

/**
 * 查询学生成绩
 */
export async function queryGrade(data: GradeParams) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;
    const canList = await canEdit(data.courseNo, data.courseSeq);
    return axios
        .post<SuccessData<Grade>>(`/teacher/grade/queryGrade`, {
            ...data,
            batchNo,
            customerCode
        })
        .then(res => res.data.data)
        .then(data => {
            const newColumns = data.columns.filter(
                item => item.weight !== "0.0%"
            );
            const newDataSource = data.dataSource.map(item => {
                if (lodash.find(canList, { studentNo: item.studentNo })) {
                    item.canEdit = "true";
                }
                return item;
            });
            return {
                columns: newColumns,
                dataSource: newDataSource,
                totalSize: data.totalSize
            };
        });
}

/**
 *下载模板
 * @param courseNo
 * @param courseSeq
 */
export function downloadExcel(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const token = getToken();
    const paramJson = JSON.stringify({
        courseNo,
        courseSeq,
        batchNo,
        token,
        customerCode: Config.CustomCode
    });
    window.open(
        `${
            Config.ApiHost
        }teacher/grade/downloadCustomGradeExcelTemplate?paramJson=${encodeURIComponent(
            paramJson
        )}`
    );
}

/**
 *下载模板 带学号姓名
 * @param courseNo
 * @param courseSeq
 */
export function downloadExcelNameCode(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const token = getToken();
    const paramJson = JSON.stringify({
        courseNo,
        courseSeq,
        batchNo,
        token,
        customerCode: Config.CustomCode
    });
    window.open(
        `${
            Config.ApiHost
        }teacher/grade/downloadCustomGradeExcelTemplateInit?paramJson=${encodeURIComponent(
            paramJson
        )}`
    );
}

/**
 * 导入学生自定义成绩
 * @param file
 * @param courseNo
 * @param courseSeq
 * @param hasWeight 是否带权重0否1是
 */
export function importCustomGrade(
    file: File,
    courseNo: string,
    courseSeq: string,
    hasWeight: "0" | "1"
) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const paramJson = JSON.stringify({
        courseNo,
        courseSeq,
        batchNo,
        customerCode: Config.CustomCode,
        hasWeight
    });
    const headers = { "Content-Type": "multipart/Form-data" };
    const formData = new FormData();
    formData.append("file", file);
    formData.append("paramJson", paramJson);
    return axios.post(`/teacher/grade/importCustomGrade`, formData, {
        headers
    });
}

export enum ImportState {
    success = "1",
    error = "2"
}

interface LogListItem {
    id: number; //日志id
    customerCode: string; //用户代码
    batchNo: string; //学期编号
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    state: number; //导入结果：1：成功 2：失败
    fileName: string; //错误文件名称
    errExcelFileUrl: string; //错误文件路径
    delFlag: number; //删除标识符0否1是
    updateTime: string;
}

/**
 * 查询导入日志
 * @param courseNo
 * @param courseSeq
 */
export function findLog(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;

    return axios
        .post<SuccessData<LogListItem[]>>(`/teacher/grade/findLog`, {
            courseNo,
            courseSeq,
            batchNo,
            customerCode: Config.CustomCode
        })
        .then(res => res.data.data);
}

/**
 * 导出失败文件
 * @param filePath
 * @returns
 */
export function downloadErrFile(filePath: string) {
    return download(
        "GET",
        `/teacher/grade/downloadErrFile?filePath=${filePath}`
    ).then(res => {
        downloadFile(res);
    });
}

/**
 * 保存学生成绩
 */
export function saveGrade(params: Record<string, GradeItemProps | undefined>) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;

    const data = {
        ...params,
        batchNo: batchNo,
        customerCode: Config.CustomCode
    };
    return axios
        .post<SuccessData<CourseListItem[]>>(`/teacher/grade/saveGrade`, {
            ...data
        })
        .then(res => res.data.data);
}

//成绩单显示模式
export interface QueryBase {
    customerCode: string; //租户代码
    batchNo: any; //学期编号
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
}
export enum GradeShowType {
    ALL = "0",
    percentage = "1",
    number = "2"
}
export interface GradeTypeProps extends QueryBase {
    config: GradeShowType;
}
export function queryDcGradeTypeSet(data: QueryBase) {
    return axios
        .post<SuccessData<GradeTypeProps>>(
            `/teacher/grade/queryDcGradeTypeSet`,
            data
        )
        .then(res => res.data.data.config);
}
//保存成绩单设置
export function saveDcGradeTypeSet(data: GradeTypeProps) {
    return axios
        .post(`/teacher/grade/saveDcGradeTypeSet`, data)
        .then(res => res.data.data);
}

/**
 * 下载学生成绩单pdf
 */
export function downloadGradePdf(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;

    const paramJson = JSON.stringify({
        courseNo,
        courseSeq,
        batchNo,
        customerCode: Config.CustomCode
    });
    return download(
        "GET",
        `/teacher/grade/downloadGrade?paramJson=${encodeURIComponent(
            paramJson
        )}`
    ).then(res => {
        downloadFile(res);
    });
}

/**
 * 下载学生成绩单Excel
 */
export function downloadGradeExcel(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;

    const paramJson = JSON.stringify({
        courseNo,
        courseSeq,
        batchNo,
        customerCode: Config.CustomCode
    });
    return download(
        "GET",
        `/teacher/grade/downloadExcel?paramJson=${encodeURIComponent(
            paramJson
        )}`
    ).then(res => {
        downloadFile(res);
    });
}

/**
 * 下载综合教务成绩
 */
export function downloadGradeTeaching(
    courseNo: string,
    courseSeq: string,
    keyword: string
) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;

    const paramJson = JSON.stringify({
        courseNo,
        courseSeq,
        batchNo,
        customerCode: Config.CustomCode,
        keyword
    });
    return download(
        "GET",
        `/teacher/grade/downloadGradeTeaching?paramJson=${encodeURIComponent(
            paramJson
        )}`
    ).then(res => {
        downloadFile(res);
    });
}

/**
 * 下载综合教务成绩2号
 * @param courseNo
 * @param courseSeq
 * @param keyword
 */
export function exportGradeTeaching(
    courseNo: string,
    courseSeq: string,
    keyword: string
) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;

    const paramJson = JSON.stringify({
        courseNo,
        courseSeq,
        keyword,
        batchNo,
        customerCode
    });

    return download(
        "GET",
        `/teacher/grade/exportGradeTeaching?paramJson=${encodeURIComponent(
            paramJson
        )}`
    ).then(downloadFile);
}

function downloadFile(res: any) {
    let fileName = window.decodeURI(
        res.headers["content-disposition"].split("=")[1]
    );

    let url = window.URL.createObjectURL(res.data);
    let link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
}

export interface PublishStatus {
    id: string; //id
    customerCode: string; //租户代码
    batchNo: string; //学期编号
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    teachUnit: string; //所属教学机构
    isProcess: BooleanValue; //是否过程化考核
    hasPublished: BooleanValue; //是否已发布（0否1是）
    hasChanged: BooleanValue; //是否更改（0否1是）
    publishStartTime: string; //允许的发布开始时间
    publishEndTime: string; //允许的发布结束时间
    publishTime: string; //发布时间
    delStatus: number; //删除标识
    createTime: string;
    updateTime: string;
    createUser: string;
    updateUser: string;
}

/**
 * 查询成绩单发布状态
 * @param courseNo
 * @param courseSeq
 */
export function queryPublishStatus(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const data = {
        courseNo,
        courseSeq,
        batchNo,
        customerCode: Config.CustomCode
    };

    return axios
        .post<SuccessData<PublishStatus>>(`/teacher/grade/queryPublishStatus`, {
            ...data
        })
        .then(res => res.data.data);
}

/**
 * 保存成绩规则附件
 */
export interface SaveFileType {
    courseNo: string;
    courseSeq: string;
    filePath: string;
    fileType: string;
    fileName: string;
}
export function saveFilePath(params: SaveFileType) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const data = {
        ...params,
        batchNo,
        customerCode: Config.CustomCode
    };
    return axios
        .post<SuccessData<PublishStatus>>(`/teacher/rule/saveFilePath`, data)
        .then(res => res.data.data);
}

interface publishGradeParams {
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    teachUnit?: string; //所属教学机构
    isProcess?: BooleanValue; //是否过程化考核（0否1是）
    hasPublished?: string; //是否发布（0否1是）
    hasChanged?: BooleanValue; //是否已更改（0否1是）
    delStatus?: number; //删除标识
}

/**
 * 发布/取消发布成绩单
 * @param params
 */
export function publishGrade(params: publishGradeParams) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const data = {
        ...params,
        batchNo,
        customerCode: Config.CustomCode
    };

    return axios.post(`/teacher/grade/publishGrade`, {
        ...data
    });
}

export enum configType {
    Integer = "0",
    Decimal = "1"
}

export interface queryDcGradeDecimalSetItem {
    customerCode: string; //租户代码
    batchNo: string; //学期编号
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    config: configType; //配置：0整数1小数
}

/**
 * 查询小数点设置
 * @param courseNo
 * @param courseSeq
 */
export function queryDcGradeDecimalSet(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const data = {
        courseNo,
        courseSeq,
        batchNo,
        customerCode: Config.CustomCode
    };

    return axios
        .post<SuccessData<queryDcGradeDecimalSetItem>>(
            `/teacher/grade/queryDcGradeDecimalSet`,
            {
                ...data
            }
        )
        .then(res => res.data.data);
}

type ReadonlyType1 = Partial<queryDcGradeDecimalSetItem>;
/**
 * 保存小数点设置
 */
export function saveDcGradeDecimalSet(params: ReadonlyType1) {
    return axios.post(`/teacher/grade/saveDcGradeDecimalSet`, {
        ...params
    });
}
/**
 * 查询需要修改成绩的学生名单
 */
export interface CanEditListItemProps {
    studentNo: string;
}
export function canEdit(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const data = {
        courseNo,
        courseSeq,
        batchNo,
        customerCode: Config.CustomCode
    };

    return axios
        .post<SuccessData<CanEditListItemProps[]>>(
            `/teacher/grade/queryChangeTask`,
            data
        )
        .then(res => res.data.data);
}
/**
 * 查询成绩单可操作状态
 */
export interface canEditProps {
    push: BooleanValue;
    importEx: BooleanValue;
}
export function getGradeOperator(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const data = {
        courseNo,
        courseSeq,
        batchNo,
        customerCode: Config.CustomCode
    };
    //可操作标识：0：不可发布、可导入自定义成绩  1：可发布、可导入自定义成绩  2：不可发布、不可导入自定义成绩  3：可发布、不可导入自定义成绩

    return axios
        .post<SuccessData<canEditProps>>(
            `/teacher/grade/getGradeOperator`,
            data
        )
        .then(res => {
            let push = false;
            let importEx = false;
            switch (res.data.message) {
                case "0":
                    importEx = true;
                    break;
                case "1":
                    push = true;
                    importEx = true;
                    break;
                case "3":
                    push = true;
            }
            return {
                push,
                importEx
            };
        });
}

//成绩单发布前检查
export enum NotEndTypes {
    SignIn = "signIn",
    Vote = "vote",
    Exercise = "exercise",
    Homework = "homework",
    GroupActivity = "groupActivity",
    Exam = "exam",
    Stu = "stu"
}
export interface NotEndAxios {
    id: string;
    title: string;
    type: NotEndTypes;
    beginTime?: string;
    endTime?: string;
    deadline?: string;
}
export interface NotEndStu {
    totalGrade: string; //总分
    studentName: string; //学生姓名
    studentNo: string; //学号
}
export interface NotEndListItemProps {
    notEndSignIn: NotEndAxios[]; //未结束的签到
    notEndVote: NotEndAxios[]; //未结束的投票
    notEndExercise: NotEndAxios[]; //未结束的练习
    notDeadlineHomework: NotEndAxios[]; //未到截止日期的作业
    notDeadlineGroupActivity: NotEndAxios[]; //未到截止日期的小组活动
    notDeadlineExam: NotEndAxios[]; //未到考试开始时间或结束时间的考试
    greatThenHundredStudents: NotEndStu[]; //超分的学生
    show: boolean;
}
export function isEndAxios(v: any): v is NotEndAxios {
    if (!v) {
        return false;
    }
    return !("studentNo" in v);
}
export function isEndStu(v: any): v is NotEndStu {
    if (!v) {
        return false;
    }
    return "studentNo" in v;
}

export function publishCheck(courseNo: string, courseSeq: string) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const data = {
        courseNo,
        courseSeq,
        batchNo,
        customerCode: Config.CustomCode
    };
    return axios
        .post<SuccessData<NotEndListItemProps>>(
            `/teacher/grade/publishCheck`,
            data
        )
        .then(res => {
            const obj: NotEndListItemProps = {
                greatThenHundredStudents:
                    res.data.data.greatThenHundredStudents,
                notDeadlineExam: res.data.data.notDeadlineExam,
                notDeadlineGroupActivity:
                    res.data.data.notDeadlineGroupActivity,
                notDeadlineHomework: res.data.data.notDeadlineHomework,
                notEndExercise: res.data.data.notEndExercise,
                notEndSignIn: res.data.data.notEndSignIn,
                notEndVote: res.data.data.notEndVote,
                show: false
            };
            return obj;
        })
        .then(res => {
            for (const resKey in res) {
                const o = res[resKey as keyof typeof res];
                if (!isEmpty(o)) {
                    res.show = true;
                }
            }
            return res;
        });
}

//结束互动
export interface entInteractionProps {
    signInIds?: string[]; //签到
    voteIds?: string[]; //投票
    exerciseIds?: string[]; //练习
}
export function endInteraction(params: entInteractionProps) {
    const data = {
        signInIds: "",
        voteIds: "",
        exerciseIds: ""
    };
    data.signInIds = params?.signInIds?.join(",") || "";
    data.voteIds = params?.voteIds?.join(",") || "";
    data.exerciseIds = params?.exerciseIds?.join(",") || "";
    return axios.post<SuccessData<any>>(`/teacher/grade/endInteraction`, data);
}

//截止互动
export interface deadLineProps {
    homeworkIds?: string[]; //作业
    groupActivityIds?: string[]; //小组活动
    examIds?: string[]; //考核
}
export function deadInteraction(params: deadLineProps) {
    const data = {
        homeworkIds: "",
        groupActivityIds: "",
        examIds: ""
    };
    data.homeworkIds = params?.homeworkIds?.join(",") || "";
    data.groupActivityIds = params?.groupActivityIds?.join(",") || "";
    data.examIds = params?.examIds?.join(",") || "";
    return axios.post<SuccessData<any>>(`/teacher/grade/deadInteraction`, data);
}

//查询课程
export interface getCourseListProps {
    batchNo: string;
    keyword: string;
}
export function postFindCourseList(params: getCourseListProps) {
    const data = {
        ...params,
        customerCode,
        remark: "0"
    };
    return axios
        .post<SuccessData<CourseListItem[]>>(
            `/teacher/rule/findCourseList`,
            data
        )
        .then(res => res.data.data);
}

//复制课程
export interface CopyProps {
    target: QueryBase;
    copy: QueryBase;
}
export function copyToCurrent(data: CopyProps) {
    return axios
        .post<SuccessData<CourseListItem[]>>(
            `/teacher/rule/copyToCurrent`,
            data
        )
        .then(res => res.data.data);
}

export interface ModelCountItem {
    id: number;
    ruleId: number;
    ruleModelName: string; // 成绩项名称
    cnt: number; // 成绩项计数
}

/**
 * 查询自定义规则计数
 * @param ruleId
 */
export async function findModelCount(ruleId: number) {
    return axios
        .get<SuccessData<ModelCountItem[]>>(
            `/teacher/rule/findModelCount/${ruleId}`
        )
        .then(res => res.data.data);
}

export interface RuleCountData {
    ruleModelName: string;
    cnt: number;
}

/**
 * 保存自定义规则计数
 * @param ruleId
 * @param data
 */
export function setRuleCount(ruleId: number, data: RuleCountData[]) {
    return axios.post(`/teacher/rule/setRuleCount/${ruleId}`, data);
}
